import { type ReactNode, useEffect } from 'react'
import styles from './Modal.module.scss'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  children?: ReactNode
  buttons?: ReactNode[]
  buttonLayout?: 'row' | 'column'
  showSeparator?: boolean
  nested?: boolean
  testId?: string
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  buttons,
  buttonLayout = 'row',
  showSeparator = false,
  nested = false,
  testId,
}: ModalProps) => {
  useEffect(() => {
    if (nested) return

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, nested])

  const backdropClassNames = [styles.backdrop, isOpen && styles.backdropOpen]
    .filter(Boolean)
    .join(' ')

  const modalClassNames = [styles.modal, isOpen && styles.modalOpen]
    .filter(Boolean)
    .join(' ')

  const buttonsClassNames = [
    buttonLayout === 'row' ? styles.buttonsRow : styles.buttonsColumn,
    showSeparator && buttonLayout === 'column' && styles.buttonsSeparator,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <div
        className={backdropClassNames}
        onClick={onClose}
        data-testid={testId ? `${testId}-backdrop` : undefined}
      />
      <div className={modalClassNames} data-testid={testId}>
        <div className={styles.handle}>
          <div className={styles.handleBar} />
        </div>

        {(title || subtitle) && (
          <div className={styles.header}>
            {title && (
              <h2
                className={styles.title}
                data-testid={testId ? `${testId}-title` : undefined}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className={styles.subtitle}
                data-testid={testId ? `${testId}-subtitle` : undefined}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {children && <div className={styles.content}>{children}</div>}

        {buttons && buttons.length > 0 && (
          <div className={styles.footer}>
            <div className={buttonsClassNames}>
              {buttons.map((button, index) => (
                <div key={index}>{button}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
